import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Clock3, Package, PackageCheck, RefreshCw } from 'lucide-react';
import { readHandovers } from './utils/handoverStorage';
import Swal from 'sweetalert2';

const decodeIds = () => {
  try {
    const ref = new URLSearchParams(window.location.search).get('ref');
    const parsed = JSON.parse(window.atob(ref || ''));
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

const getTrackingCacheKey = () => {
  const ref = new URLSearchParams(window.location.search).get('ref') || 'invalid';
  return `handover_tracking_cache_${ref}`;
};

const readTrackingCache = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(getTrackingCacheKey()) || 'null');
    return cached && Array.isArray(cached.items) ? cached : { items: [], synced_at: null };
  } catch {
    return { items: [], synced_at: null };
  }
};

const isSameData = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const formatTime = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' });
};

const methodLabel = method => ({
  qr: 'Barcode',
  manual: 'Input Nama',
  unattended: 'Foto + Keterangan',
}[method] || '');

export default function HandoverTrackingPage() {
  const ids = useMemo(decodeIds, []);
  const initialCache = useMemo(() => {
    const trackingCache = readTrackingCache();
    if (trackingCache.items.length) return trackingCache;

    const localItems = readHandovers().filter(item => ids.includes(String(item.id)));
    return {
      items: localItems,
      synced_at: localItems.length ? new Date().toISOString() : null,
    };
  }, [ids]);
  const [items, setItems] = useState(initialCache.items);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(initialCache.synced_at ? new Date(initialCache.synced_at) : null);
  const [receivingId, setReceivingId] = useState(null);

  const load = async () => {
    setLoading(true);
    let source = [];
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch('https://jne-cilacap.com/slip/public/api/handovers', {
        signal: controller.signal,
      });
      const result = await response.json();
      if (result.status === 'success' && Array.isArray(result.data)) source = result.data;
    } catch {
      source = readHandovers();
    } finally {
      window.clearTimeout(timeoutId);
    }
    if (!source.length) source = readHandovers();
    const latestItems = source.filter(item => ids.includes(String(item.id)));
    const syncTime = new Date();

    setItems(currentItems => {
      if (!latestItems.length || isSameData(currentItems, latestItems)) return currentItems;
      localStorage.setItem(getTrackingCacheKey(), JSON.stringify({
        items: latestItems,
        synced_at: syncTime.toISOString(),
      }));
      return latestItems;
    });
    setLastUpdated(syncTime);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const interval = window.setInterval(load, 15000);
    return () => window.clearInterval(interval);
  }, []);

  const receiveItem = async (item) => {
    const confirmation = await Swal.fire({
      icon: 'question',
      title: 'Terima Barang?',
      text: `Konfirmasikan bahwa ${item.item_name} sudah diterima oleh ${item.recipient_name}.`,
      showCancelButton: true,
      confirmButtonText: 'Ya, Sudah Diterima',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#10b981',
    });
    if (!confirmation.isConfirmed) return;

    setReceivingId(item.id);
    try {
      const response = await fetch('https://jne-cilacap.com/slip/public/api/handovers/receive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scanned_text: item.id,
          user_id: String(item.recipient_id || `external:${item.id}`),
          user_name: item.recipient_name,
          receive_method: 'public_tracking_link',
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status !== 'success') {
        throw new Error(result.message || 'Penerimaan belum dapat disimpan.');
      }

      await Swal.fire({
        icon: 'success',
        title: 'Barang Diterima',
        text: 'Status dan timeline handover telah diperbarui.',
        timer: 1700,
        showConfirmButton: false,
      });
      await load();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal Mengonfirmasi',
        text: error.message || 'Silakan coba kembali.',
      });
    } finally {
      setReceivingId(null);
    }
  };

  if (!ids.length) {
    return <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#f8fafc', padding: 24, textAlign: 'center', color: '#64748b' }}>Link tracking tidak valid.</div>;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '28px 16px 60px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <style>{`
          @keyframes handover-sync-spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <div style={{ width: 58, height: 58, display: 'grid', placeItems: 'center', borderRadius: 18, background: '#eff6ff', margin: '0 auto 10px' }}><Package color="#2563eb" size={30} /></div>
          <h1 style={{ fontSize: 21, color: '#1e293b', margin: 0 }}>Tracking Serah Terima</h1>
          <p style={{ color: '#64748b', fontSize: 12, margin: '6px 0 0' }}>Halaman diperbarui otomatis setiap 15 detik</p>
          <button onClick={load} disabled={loading} style={{ marginTop: 11, border: 'none', borderRadius: 10, padding: '8px 12px', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            <RefreshCw size={13} style={{ marginRight: 6, verticalAlign: 'middle', animation: loading ? 'handover-sync-spin .75s linear infinite' : 'none' }} />
            {loading ? ' Menyinkronkan...' : ' Perbarui'}
          </button>
        </div>

        {loading && !items.length ? (
          <div style={{ padding: 36, textAlign: 'center', color: '#64748b' }}>Memuat progres handover...</div>
        ) : !items.length ? (
          <div style={{ padding: 28, borderRadius: 18, background: '#fff', border: '1px solid #e2e8f0', textAlign: 'center', color: '#64748b' }}>
            Data belum tersedia di server. Silakan coba kembali beberapa saat lagi.
          </div>
        ) : items.map(item => {
          const timeline = item.timeline?.length ? item.timeline : [{
            status: 'Dibuat',
            actor_name: item.sender_name,
            timestamp: item.timestamp || item.created_at,
            notes: `Handover dibuat oleh ${item.sender_name}`,
          }];
          const complete = ['selesai', 'diterima'].includes(String(item.status).toLowerCase());
          return (
            <div key={item.id} style={{ background: '#fff', borderRadius: 22, padding: 20, marginBottom: 16, boxShadow: '0 4px 20px rgba(15,23,42,.06)', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', paddingBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 28 }}>{item.category === 'Dokumen' ? '📄' : '📦'}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#1e293b' }}>{item.item_name}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>Tujuan: {item.recipient_name} · {item.kantor}</div>
                </div>
                <span style={{ padding: '5px 8px', borderRadius: 8, fontSize: 10, fontWeight: 800, background: complete ? '#dcfce7' : '#fef3c7', color: complete ? '#166534' : '#92400e' }}>{item.status || 'Diproses'}</span>
              </div>

              {!complete && (
                <button
                  type="button"
                  disabled={receivingId === item.id}
                  onClick={() => receiveItem(item)}
                  style={{
                    width: '100%',
                    marginTop: 14,
                    padding: '12px 14px',
                    border: 'none',
                    borderRadius: 12,
                    background: receivingId === item.id ? '#a7f3d0' : '#10b981',
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 800,
                    cursor: receivingId === item.id ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <PackageCheck size={17} />
                  {receivingId === item.id ? 'Menyimpan Penerimaan...' : 'Terima Barang'}
                </button>
              )}

              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#475569', marginBottom: 16 }}>Alur & Timeline Serah Terima</div>
                {timeline.map((event, index) => {
                  const eventComplete = ['selesai', 'diterima'].includes(String(event.status).toLowerCase());
                  return (
                    <div key={`${event.timestamp}-${index}`} style={{ display: 'flex', gap: 12, position: 'relative', paddingBottom: index < timeline.length - 1 ? 22 : 0 }}>
                      {index < timeline.length - 1 && <div style={{ position: 'absolute', left: 7, top: 16, bottom: 0, width: 2, background: '#e2e8f0' }} />}
                      <div style={{ zIndex: 1, width: 16, height: 16, borderRadius: '50%', background: eventComplete ? '#10b981' : '#2563eb', border: '3px solid #fff', boxShadow: '0 0 0 2px #dbeafe', boxSizing: 'border-box', marginTop: 2 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 800, color: '#1e293b' }}>
                          {eventComplete ? <CheckCircle2 size={14} color="#10b981" /> : <Clock3 size={14} color="#2563eb" />}
                          {event.status || 'Dibuat'}
                        </div>
                        <div style={{ fontSize: 12, color: '#475569', marginTop: 3 }}>{event.notes || `Oleh ${event.actor_name}`}</div>
                        {event.method && <span style={{ display: 'inline-block', marginTop: 5, padding: '3px 7px', borderRadius: 7, background: '#e0e7ff', color: '#4338ca', fontSize: 10, fontWeight: 700 }}>{methodLabel(event.method)}</span>}
                        {event.photo && <img src={event.photo} alt="Bukti penyerahan" style={{ display: 'block', width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 12, marginTop: 8 }} />}
                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 5 }}>{formatTime(event.timestamp)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {lastUpdated && <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 10 }}>Terakhir diperbarui: {formatTime(lastUpdated)}</div>}
      </div>
    </div>
  );
}
